import { cloneData } from '../model/clone.ts';
import { newId } from '../model/ids.ts';
import { PARAM_DEFINITIONS } from '../model/params.ts';
import type { Animatable, Clip, Easing, Keyframe, ParamKey } from '../model/schema.ts';

export function applyEasing(easing: Easing, t: number): number {
  switch (easing) {
    case 'linear':
      return t;
    case 'hold':
      return 0;
    case 'easeIn':
      return t * t * t;
    case 'easeOut':
      return 1 - Math.pow(1 - t, 3);
    case 'easeInOut':
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
}

/**
 * Evaluates an animatable value at a (possibly fractional) frame relative to the clip start.
 * Before the first / after the last keyframe the value is held constant.
 */
export function evaluateAnimatable(anim: Animatable, frame: number): number {
  const kfs = anim.keyframes;
  if (kfs.length === 0) return anim.value;
  const first = kfs[0]!;
  if (frame <= first.frame) return first.value;
  const last = kfs[kfs.length - 1]!;
  if (frame >= last.frame) return last.value;
  // Binary search for the segment [a, b] containing frame.
  let lo = 0;
  let hi = kfs.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (kfs[mid]!.frame <= frame) lo = mid;
    else hi = mid;
  }
  const a = kfs[lo]!;
  const b = kfs[hi]!;
  const t = (frame - a.frame) / (b.frame - a.frame);
  return a.value + (b.value - a.value) * applyEasing(a.easing, t);
}

/** Evaluates a clip parameter, falling back to the registry default. */
export function evaluateParam(clip: Clip, key: ParamKey, frame: number): number {
  const anim = clip.params[key];
  return anim ? evaluateAnimatable(anim, frame) : PARAM_DEFINITIONS[key].defaultValue;
}

export function isAnimated(anim: Animatable | undefined): boolean {
  return !!anim && anim.keyframes.length > 0;
}

export function staticAnimatable(value: number): Animatable {
  return { value, keyframes: [] };
}

/** Inserts or replaces a keyframe at `frame` (mutates; intended for immer drafts). */
export function upsertKeyframe(anim: Animatable, frame: number, value: number, easing?: Easing): Keyframe {
  const existing = anim.keyframes.find((k) => k.frame === frame);
  if (existing) {
    existing.value = value;
    if (easing) existing.easing = easing;
    return existing;
  }
  const kf: Keyframe = { id: newId('kf'), frame, value, easing: easing ?? 'linear' };
  const idx = anim.keyframes.findIndex((k) => k.frame > frame);
  if (idx === -1) anim.keyframes.push(kf);
  else anim.keyframes.splice(idx, 0, kf);
  return kf;
}

export function sortKeyframes(anim: Animatable): void {
  anim.keyframes.sort((a, b) => a.frame - b.frame);
}

/** Frames (relative) where a value changes non-linearly or has a corner; used for audio envelopes. */
export function keyframeFrames(anim: Animatable | undefined): number[] {
  return anim ? anim.keyframes.map((k) => k.frame) : [];
}

/**
 * Splits an animatable at a relative frame into the parts for the left clip (`[0, at)`) and the
 * right clip (re-based so that `at` becomes frame 0). Values at the split point are preserved by
 * inserting boundary keyframes, so the visible animation does not change.
 */
export function splitAnimatable(anim: Animatable, at: number): [Animatable, Animatable] {
  if (anim.keyframes.length === 0) return [cloneData(anim), cloneData(anim)];
  const valueAt = evaluateAnimatable(anim, at);
  const left: Animatable = { value: anim.value, keyframes: [] };
  const right: Animatable = { value: anim.value, keyframes: [] };
  // Easing of the segment that spans the cut, so both halves keep the same "shape" family.
  const spanning = [...anim.keyframes].reverse().find((k) => k.frame <= at);
  for (const k of anim.keyframes) {
    if (k.frame < at) left.keyframes.push({ ...k });
    else right.keyframes.push({ ...k, id: newId('kf'), frame: k.frame - at });
  }
  if (!left.keyframes.some((k) => k.frame === at)) {
    left.keyframes.push({ id: newId('kf'), frame: at, value: valueAt, easing: 'linear' });
  }
  if (!right.keyframes.some((k) => k.frame === 0)) {
    right.keyframes.unshift({
      id: newId('kf'),
      frame: 0,
      value: valueAt,
      easing: spanning?.easing ?? 'linear',
    });
  }
  return [left, right];
}

/** Shifts all keyframes by `delta` frames (used when trimming a clip's start). */
export function shiftAnimatable(anim: Animatable, delta: number): void {
  for (const k of anim.keyframes) k.frame += delta;
}
